import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import AdminConfig from './components-container/pages/AdminConfig';
import AdminConfigClients from './components-container/pages/AdminConfigClients';
import AdminConfigDevicesEdit from './components-container/pages/AdminConfigDevicesEdit';
import AdminConfigTractorNozzles from './components-container/pages/AdminConfigTractorNozzles';
import AdminConfigTractorBooms from './components-container/pages/AdminConfigTractorBooms';
import AdminConfigAlerts from './components-container/pages/AdminConfigAlerts';
import AdminConfigTractorHistory from './components-container/pages/AdminConfigTractorHistory';
import AdminConfigTractorSprayProfiles from './components-container/pages/AdminConfigTractorSprayProfiles';
import AdminConfigTractorNozzleProfiles from './components-container/pages/AdminConfigTractorNozzleProfiles';
import AdminConfigHealth from './components-container/pages/AdminConfigHealth';
import AdminSummaryDashboard from './components-container/pages/AdminSummaryDashboard';
import AdminConfigTractors from './components-container/pages/AdminConfigTractors';
import AdminConfigCommands from './components-container/pages/AdminConfigCommands';
import ApiAuthorzationRoutes from './components-container/api-authorization/ApiAuthorizationRoutes';
import Home from './components-container/pages/Home';
import RunDetails from './components-container/pages/RunDetails';
import TechSupport from './components-container/pages/TechSupport';
import TractorDetails from './components-container/pages/TractorDetails';
import Tractors from './components-container/pages/Tractors';
import UserManagementContent from './components-container/pages/SettingsPages/UserManagement/UserManagementContent';
import Savings from './components-container/pages/Savings';
import Reports from './components-container/pages/Reports';
import Dashboard from './components-container/pages/Dashboard';
import BlockDetails from './components-container/pages/BlockDetails';
import FlowCalibration from './components-container/pages/AdminConfigTractors/FlowCalibration';
import AdminDeviceDiagnostic from './components-container/pages/AdminDeviceDiagnostic/AdminDeviceDiagnostic';
import BlocksView from './components-container/pages/BlocksView/BlocksView';
import BlocksImport from './components-container/pages/BlocksImport/BlocksImport';
import Notifications from './components-container/pages/Notifications';

const RouteFallback = () => (
  <LoadingOverlay
    loaderProps={{ color: 'green' }}
    visible
    overlayBlur={4}
    overlayOpacity={1}
    id="loading-overlay"
  />
);

const AppRoutes = [
  {
    path: '/',
    index: true,
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Home />
      </Suspense>
    ),
    requireAdmin: true,
    breadcrumb: 'Home'
  },
  {
    path: '/home',
    index: true,
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Home />
      </Suspense>
    ),
    requireAuth: true,
    breadcrumb: 'Home'
  },
  {
    path: '/reports',
    requireManager: true,
    breadcrumb: 'Reports',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Reports />
      </Suspense>
    )
  },
  {
    path: '/savings',
    requireManager: true,
    breadcrumb: 'Savings',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Savings />
      </Suspense>
    )
  },
  {
    path: '/precision-spray/:tractor/:tractorId/run-details/:runId/:runIndex',
    breadcrumb: ({ match, ...rest }) => {
      return (
        <span className="capitalize">Run Details {match.params.runIndex}</span>
      );
    },

    element: (
      <Suspense fallback={<RouteFallback />}>
        <RunDetails />
      </Suspense>
    )
  },
  {
    path: '/dashboard/block-details/:blockName/:subLocationId',
    requireAuth: true,
    breadcrumb: ({ match, ...rest }) => {
        console.log("match", match);
        console.log("rest", rest);
      return (
        <span className="capitalize">{match.params.blockName.replaceAll('-', ' ')}</span>
      );
    },

    element: (
      <Suspense fallback={<RouteFallback />}>
        <BlockDetails />
      </Suspense>
    )
  },
  {
    path: '/precision-spray',
    breadcrumb: 'Sprayers',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Tractors />
      </Suspense>
    )
  },
  {
    path: '/precision-spray/:name/:id',
    breadcrumb: ({ match, ...rest }) => {
      return (
        <span className="capitalize">
          {match.params.name.replaceAll('-', ' ')}
        </span>
      );
    },
    element: (
      <Suspense fallback={<RouteFallback />}>
        <TractorDetails />
      </Suspense>
    )
  },
  {
    path: '/tech-support',
    breadcrumb: 'Tech Support',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <TechSupport />
      </Suspense>
    )
  },
  {
    path: '/notifications',
    requireManager: true,
    breadcrumb: 'Notifications',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Notifications />
      </Suspense>
    )
  },
  {
    path: '/settings/user-management',
    requireManager: true,
    breadcrumb: 'User Management',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <UserManagementContent />
      </Suspense>
    )
  },
  {
    path: '/settings/blocks-view',
    requireManager: true,
    breadcrumb: 'View Blocks',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <BlocksView />
      </Suspense>
    )
  },
  {
    path: '/settings/blocks-import',
    requireManager: true,
    breadcrumb: 'Import Blocks',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <BlocksImport />
      </Suspense>
    )
  },
  {
    path: '/admin',
    requireAdmin: true,
    breadcrumb: 'Admin',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminConfig />
      </Suspense>
    )
  },
  {
    path: '/admin/clients',
    requireAdmin: true,
    breadcrumb: 'Clients',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminConfigClients />
      </Suspense>
    )
  },
  {
    path: '/admin/device/:id',
    requireAuth: true,
    requireAdmin: true,
    breadcrumb: 'Edit Device',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminConfigDevicesEdit />
      </Suspense>
    )
  },
  {
    path: '/admin/sprayers/flow-calibration',
    requireAuth: true,
    requireAdmin: true,
    breadcrumb: 'Calibrate Flowmeter',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <FlowCalibration />
      </Suspense>
    )
  },
  {
    path: '/admin/sprayers/device-diagnostic',
    requireAuth: true,
    requireAdmin: true,
    breadcrumb: 'Flow Diagnostic',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminDeviceDiagnostic />
      </Suspense>
    )
  },
  {
    path: '/admin/sprayers/nozzles',
    requireAdmin: true,
    breadcrumb: 'Nozzles',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminConfigTractorNozzles />
      </Suspense>
    )
  },

  {
    path: '/admin/sprayers/booms',
    breadcrumb: 'Booms',
    requireAdmin: true,
    element: (
        <Suspense fallback={<RouteFallback />}>
            <AdminConfigTractorBooms />
        </Suspense>
    )
  },

  {
    path: '/admin/sprayers/alerts',
    breadcrumb: 'Alerts',
    requireAdmin: true,
    element: (
        <Suspense fallback={<RouteFallback />}>
        <AdminConfigAlerts />
        </Suspense>
    )
  },

  {
    path: '/admin/sprayers/history',
    breadcrumb: 'History',
    requireAdmin: true,
    element: (
        <Suspense fallback={<RouteFallback />}>
            <AdminConfigTractorHistory />
        </Suspense>
    )
  },
  
  {
    path: '/admin/sprayers/sprayprofiles',
    requireAdmin: true,
    breadcrumb: 'Spray Profiles',
    element: (
        <Suspense fallback={<RouteFallback />}>
            <AdminConfigTractorSprayProfiles />
        </Suspense>
    )
  },

  {
    path: '/admin/sprayers/nozzleprofiles',
    requireAdmin: true,
    breadcrumb: 'Nozzle Profiles',
    element: (
        <Suspense fallback={<RouteFallback />}>
            <AdminConfigTractorNozzleProfiles />
        </Suspense>
    )
  },

  {
    path: '/admin/health',
    requireAdmin: true,
    breadcrumb: 'Health',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminConfigHealth />
      </Suspense>
    )
  },

  {
    path: '/admin/summary-dashboard',
    requireAdmin: true,
    breadcrumb: 'Summary Dashboard',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminSummaryDashboard />
      </Suspense>
    )
  },

  {
    path: '/admin/sprayers',
    requireAdmin: true,
    breadcrumb: 'Sprayers',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminConfigTractors />
      </Suspense>
    )
  },

  {
    path: '/admin/sprayers/device-commands',
    requireAdmin: true,
    breadcrumb: 'Device Commands',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AdminConfigCommands />
      </Suspense>
    )
  },
  {
    path: '/dashboard',
    requireAuth: true,
    breadcrumb: 'Dashboard',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Dashboard />
      </Suspense>
    )
  },
  {
    path: '/undefined',
    element: <Navigate to="/" replace={true} />
  },
  ...ApiAuthorzationRoutes
];

export default AppRoutes;
